import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dropdown, DropdownContent, DropdownItem, DropdownTrigger, Icon, TextButton } from '../../../components';
import { AppRoutes } from '../../../constants';
import { useAppSelector, useFeatureFlag } from '../../../hooks';
import useGetUserPermissions from '../../../hooks/useGetUserPermissions';
import { DropdownPlacement, LD_FeatureFlags, OrganizationStatus, Permissions } from '../../../types';
import { conditionalArray } from '../../../utils';
import { ManageFolderModal } from '../../modals';

const CREATE_DROPDOWN_MIN_WIDTH = 150;

const CreateButton = () => {
  const navigate = useNavigate();
  const coachingRubricsFF = useFeatureFlag(LD_FeatureFlags.RELEASE_COACHING_RUBRICS);
  const { organization, user } = useAppSelector((state) => state.auth);

  const isSelfServiceEnabled = !!organization?.isSelfServiceEnabled;
  const isTrialOrg = organization?.status === OrganizationStatus.TRIAL;

  const permissions = user?.permissions || [];
  const canModifyScorecardTemplates = permissions.includes(Permissions.MODIFY_SCORECARD_TEMPLATE);
  const canModifyCoachingTemplates = permissions.includes(Permissions.MODIFY_COACHING_TEMPLATE);

  const canManageScoringAndCoaching = canModifyScorecardTemplates || (canModifyCoachingTemplates && coachingRubricsFF);

  const { canUserCreateSims, canUserEditLibrary } = useGetUserPermissions();

  const [isCreateDropdownOpen, setIsCreateDropdownOpen] = useState(false);
  const [isCreateFolderModalOpen, setIsCreateFolderModalOpen] = useState(false);

  const items = useMemo(
    () => [
      ...conditionalArray(canUserCreateSims, {
        icon: Icon.USER_PLUS,
        label: 'Simulation',
        onClick: () => navigate(AppRoutes.PROSPECT),
      }),
      ...conditionalArray(canManageScoringAndCoaching, {
        icon: Icon.SCORECARD,
        label: 'Scoring & coaching',
        disabled: isTrialOrg,
        onClick: () => navigate(AppRoutes.SCORING_AND_COACHING),
      }),
      ...conditionalArray(canUserEditLibrary, {
        icon: Icon.FOLDER_PLUS,
        label: 'Folder',
        onClick: () => {
          setIsCreateFolderModalOpen(true);
          setIsCreateDropdownOpen(false);
        },
      }),
    ],
    [canUserCreateSims, canUserEditLibrary, canModifyScorecardTemplates, navigate, isTrialOrg]
  );

  const isCreateDisabled = !items.length || !isSelfServiceEnabled;

  return (
    <>
      <Dropdown
        minWidth={CREATE_DROPDOWN_MIN_WIDTH}
        open={isCreateDropdownOpen}
        placement={DropdownPlacement.BOTTOM_END}
        onOpenChange={setIsCreateDropdownOpen}
      >
        <DropdownTrigger asChild>
          <TextButton
            text="Create"
            startIcon={isCreateDisabled ? Icon.LOCK : Icon.PLUS}
            disabled={isCreateDisabled}
            onClick={() => setIsCreateDropdownOpen(true)}
          />
        </DropdownTrigger>
        <DropdownContent>
          {items.map((item) => (
            <DropdownItem key={item.label} {...item} />
          ))}
        </DropdownContent>
      </Dropdown>
      {isCreateFolderModalOpen && <ManageFolderModal closeModal={() => setIsCreateFolderModalOpen(false)} />}
    </>
  );
};

export default CreateButton;
